import {
  DEFAULT_GAMEPLAY_WAVE_SETTINGS,
  GAMEPLAY_WAVE_MODES,
  GAMEPLAY_WAVE_STYLES,
  INTERACTION_MODES,
  RENDER_MODES,
  DEFAULT_RECORDING_CONFIG,
  PALETTE_IDS,
  RECORD_FPS,
  elapsedLabel,
  recordingSizeLabel,
  type LiveRecordingConfig,
  type LiveRecordingStatus,
  type RecordFps,
  SUGGESTED_GAMEPLAY_WEIGHTS,
  paletteColor,
  resolvePalette,
  type GameplayWaveMode,
  type GameplayWaveSettings,
  type GameplayWaveStyle,
  type ArtStateValues,
  type InteractionMode,
  type RenderMode,
  type VisualLayerReport,
} from '../visual/index.js';

/**
 * Controls for the art engine, folded away.
 *
 * Collapsed by default and anchored to a corner, because the brief is explicit
 * that the play surface must not fill up with settings. Open it, change a layer,
 * close it; the panel is a tool, not part of the piece.
 *
 * It offers only what this build actually does. There is no SWITCH/RANDOM/AUTO
 * selector here because those render modes are not implemented yet, and a
 * control that silently does nothing is worse than an absent one.
 */

const STYLE_ID = 'mandalizm-visual-style';

const CSS = `
.mandalizm-visual {
  /*
   * Laid out by the dock rather than pinned to the corner itself. Both this
   * panel and the export panel used to be fixed to the bottom right, and an
   * open one covered the other completely — including the other's toggle, so
   * there was no way back. The dock stacks them instead.
   */
  position: relative; width: 100%;
  font: 11px/1.6 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; color: #bff4ee;
}
.mandalizm-visual[hidden] { display: none; }
.mandalizm-visual__toggle {
  appearance: none; cursor: pointer; font: inherit; letter-spacing: 0.18em;
  width: 100%; padding: 9px 12px; color: #cdf6f1; text-align: left;
  background: rgba(3, 26, 33, 0.88); border: 1px solid rgba(90, 240, 220, 0.28);
  border-radius: 6px;
}
.mandalizm-visual__toggle:hover { background: rgba(6, 70, 78, 0.92); }
.mandalizm-visual__body {
  margin-top: 6px; padding: 12px; box-sizing: border-box;
  background: rgba(0, 12, 18, 0.9); border: 1px solid rgba(90, 240, 220, 0.22);
  border-radius: 6px; max-height: 52vh; overflow-y: auto;
}
.mandalizm-visual__body[hidden] { display: none; }
.mandalizm-visual__group { margin-bottom: 12px; }
.mandalizm-visual__label {
  font-size: 9px; letter-spacing: 0.24em; color: rgba(140, 205, 220, 0.65);
  display: block; margin-bottom: 5px;
}
.mandalizm-visual__row { display: flex; gap: 5px; flex-wrap: wrap; }
.mandalizm-visual__chip {
  appearance: none; cursor: pointer; font: inherit; font-size: 10px; letter-spacing: 0.1em;
  flex: 1 1 auto; padding: 6px 8px; color: #a9dcd8;
  background: rgba(4, 30, 38, 0.9); border: 1px solid rgba(90, 240, 220, 0.22);
  border-radius: 4px;
}
.mandalizm-visual__chip[aria-pressed="true"] {
  color: #eafeff; background: rgba(6, 86, 92, 0.95); border-color: rgba(120, 250, 232, 0.6);
}
.mandalizm-visual__layer {
  padding: 8px; margin-bottom: 6px; border-radius: 5px;
  background: rgba(4, 24, 30, 0.7); border: 1px solid rgba(90, 240, 220, 0.14);
}
.mandalizm-visual__layer--faulted { border-color: rgba(255, 140, 150, 0.5); }
.mandalizm-visual__head { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
.mandalizm-visual__name { flex: 1 1 auto; color: #eafeff; }
.mandalizm-visual__slider { display: grid; grid-template-columns: 54px 1fr 34px; gap: 6px; align-items: center; }
.mandalizm-visual__slider input { width: 100%; }
.mandalizm-visual__value { text-align: right; font-variant-numeric: tabular-nums; }
.mandalizm-visual__error { color: #ff9ab4; margin-top: 4px; word-break: break-word; }
.mandalizm-visual__note { color: rgba(150, 215, 225, 0.6); margin-top: 8px; }
.mandalizm-visual__seed {
  flex: 2 1 120px; min-width: 0; font: inherit; font-size: 10px; padding: 6px 8px;
  color: #eafeff; background: rgba(2, 18, 24, 0.9);
  border: 1px solid rgba(90, 240, 220, 0.22); border-radius: 4px;
}
.mandalizm-visual__art {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 3px 10px; margin-top: 8px;
  font-size: 10px; color: rgba(150, 215, 225, 0.75);
}
.mandalizm-visual__art b { color: #eafeff; font-variant-numeric: tabular-nums; float: right; }
/* The gameplay wave controls are tinted apart from the artwork controls on
   purpose. They are a different setting that happens to name the same
   materials, and a user who reads them as "the layer list again" will expect
   changing one to change the other. */
.mandalizm-visual__group--gameplay {
  padding: 8px; border-radius: 5px; margin-top: 4px;
  background: rgba(38, 16, 40, 0.55); border: 1px solid rgba(226, 130, 240, 0.28);
}
.mandalizm-visual__group--gameplay .mandalizm-visual__label { color: rgba(238, 170, 250, 0.8); }
.mandalizm-visual__group--gameplay .mandalizm-visual__chip {
  background: rgba(30, 10, 34, 0.9); border-color: rgba(226, 130, 240, 0.25); color: #e9c4f2;
}
.mandalizm-visual__group--gameplay .mandalizm-visual__chip[aria-pressed="true"] {
  color: #fff2ff; background: rgba(112, 30, 124, 0.95); border-color: rgba(240, 170, 250, 0.65);
}
.mandalizm-visual__gameplay-note { font-size: 10px; color: rgba(226, 170, 236, 0.6); margin-top: 5px; }
/* Recording is tinted red and boxed for one reason: it must never be mistaken
   for SAVE REPLAY. One writes a JSON of what the player did, the other writes a
   video of what the screen showed, and a user who confuses them loses work. */
.mandalizm-visual__group--record {
  padding: 8px; border-radius: 5px; margin-top: 4px;
  background: rgba(44, 12, 16, 0.55); border: 1px solid rgba(255, 120, 130, 0.28);
}
.mandalizm-visual__group--record .mandalizm-visual__label { color: rgba(255, 165, 170, 0.8); }
.mandalizm-visual__group--record .mandalizm-visual__chip {
  background: rgba(34, 10, 12, 0.9); border-color: rgba(255, 120, 130, 0.25); color: #f2c8cc;
}
.mandalizm-visual__group--record .mandalizm-visual__chip[aria-pressed="true"] {
  color: #fff2f3; background: rgba(126, 26, 34, 0.95); border-color: rgba(255, 160, 168, 0.65);
}
.mandalizm-visual__chip--rec { color: #fff; background: rgba(200, 40, 52, 0.9); font-weight: 700; }
.mandalizm-visual__chip--rec[disabled] { opacity: 0.34; }
.mandalizm-visual__rec-status {
  margin-top: 6px; font-size: 10px; color: #ffd9dc; font-variant-numeric: tabular-nums;
}
.mandalizm-visual__rec-status b { color: #ff6b78; }
.mandalizm-visual__rec-error {
  margin-top: 6px; padding: 6px 7px; border-radius: 4px; font-size: 10px;
  background: rgba(70, 8, 24, 0.6); border: 1px solid rgba(255, 140, 160, 0.5); color: #ffc2ce;
}
.mandalizm-visual__rec-done {
  margin-top: 6px; padding: 7px; border-radius: 4px; font-size: 10px;
  background: rgba(30, 10, 14, 0.8); border: 1px solid rgba(255, 140, 150, 0.4); color: #ffd9dc;
}
.mandalizm-visual__rec-done b { color: #fff; word-break: break-all; }
/* A palette chip shows its own colours. Reading "COOL" tells you nothing about
   what the run will look like; four squares of it do. */
.mandalizm-visual__swatch {
  display: flex; gap: 1px; margin-top: 4px; height: 5px; border-radius: 2px; overflow: hidden;
}
.mandalizm-visual__swatch span { flex: 1 1 auto; }
.mandalizm-visual__group--gameplay .mandalizm-visual__chip--palette {
  display: flex; flex-direction: column; align-items: stretch; padding: 5px 6px 4px;
}
`;

export interface VisualPanelHandlers {
  readonly onInteractionMode: (mode: InteractionMode) => void;
  readonly onRenderMode: (mode: RenderMode) => void;
  readonly onSeed: (seed: number) => void;
  readonly onEngineEnabled: (enabled: boolean) => void;
  readonly onLayerEnabled: (id: string, enabled: boolean) => void;
  readonly onLayerOpacity: (id: string, opacity: number) => void;
  readonly onLayerIntensity: (id: string, intensity: number) => void;
  readonly onAddLayer: (renderer: string) => void;
  readonly onRemoveLayer: (id: string) => void;
  readonly onSaveRecipe: () => void;
  readonly onLoadRecipe: (file: File) => void;
  readonly onRandomize: () => void;
  readonly onSaveReplay: () => void;
  readonly onLoadReplay: (file: File) => void;
  readonly onPlayReplay: (recipe: ReplayRecipeChoice) => void;
  /** Gameplay wave visuals. Separate from the layer handlers above. */
  readonly onGameplayWave: (settings: GameplayWaveSettings) => void;
  /** Live capture. Nothing to do with the replay handlers above. */
  readonly onRecordingConfig: (config: LiveRecordingConfig) => void;
  readonly onStartRecording: () => void;
  readonly onStopRecording: () => void;
  readonly onCancelRecording: () => void;
  readonly onDownloadRecording: () => void;
  /**
   * Fired when this panel's body opens.
   *
   * Both panels are pinned to the same corner and both are tall enough to cover
   * the other, so whoever is on top swallows every click aimed at the one
   * underneath. The session uses this to close the other one.
   */
  readonly onOpenChanged?: (open: boolean) => void;
}

/** What a finished capture is, for the panel to describe. */
export interface RecordingSummary {
  readonly fileName: string;
  readonly bytes: number;
  readonly durationSeconds: number;
  readonly width: number;
  readonly height: number;
  readonly fps: number;
  readonly hasAudio: boolean;
  readonly mimeType: string;
}

/** Which interpretation a replay should be played through. */
export type ReplayRecipeChoice = 'original' | 'current' | 'randomize';

/**
 * A palette colour as CSS, lifted so it is visible in a five-pixel strip.
 *
 * The palette is authored in the 0.12–0.40 range because the stage's tone curve
 * destroys saturation above that — correct on screen, and indistinguishable
 * from black in a swatch. The lift is presentation only and never reaches a
 * renderer.
 */
function swatchCss(colour: readonly [number, number, number]): string {
  const lift = (value: number): number => Math.round(Math.min(1, value * 2.4) * 255);
  return `rgb(${lift(colour[0])}, ${lift(colour[1])}, ${lift(colour[2])})`;
}

const MODE_LABELS: Readonly<Record<GameplayWaveMode, string>> = {
  FIXED: 'FIXED',
  RANDOM: 'RANDOM',
  SHUFFLE: 'SHUFFLE',
  WEIGHTED_RANDOM: 'WEIGHTED',
  AUDIO_REACTIVE: 'AUDIO',
};

/** One line saying what the current setting will actually do. */
function describeGameplay(settings: GameplayWaveSettings): string {
  switch (settings.mode) {
    case 'FIXED':
      return `every wave is ${settings.defaultStyle}`;
    case 'RANDOM':
      return 'each wave picks a style from its own seed';
    case 'SHUFFLE':
      return 'every style takes a turn before any repeats';
    case 'WEIGHTED_RANDOM': {
      const named = (Object.entries(settings.styles) as [GameplayWaveStyle, number][])
        .filter(([, weight]) => weight > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([style, weight]) => `${style} ${weight}`);
      return named.length > 0 ? `weighted — ${named.join(', ')}…` : 'no weights set';
    }
    case 'AUDIO_REACTIVE':
      return 'bass → smoke/ink, treble → code/math, quiet → sumi/wash';
    default:
      return '';
  }
}

export class VisualPanel {
  readonly element: HTMLDivElement;

  private readonly body: HTMLDivElement;
  private readonly toggle: HTMLButtonElement;
  private readonly modeRow: HTMLDivElement;
  private readonly renderRow: HTMLDivElement;
  private readonly seedInput: HTMLInputElement;
  private readonly layerList: HTMLDivElement;
  private readonly addRow: HTMLDivElement;
  private readonly artRow: HTMLDivElement;
  private readonly note: HTMLDivElement;
  private readonly loadInput: HTMLInputElement;
  private readonly enableChip: HTMLButtonElement;
  private readonly replayInput: HTMLInputElement;
  private readonly replayNote: HTMLDivElement;
  private readonly saveReplayButton: HTMLButtonElement;
  private readonly replayButtons: HTMLButtonElement[];
  private readonly gameplayStyleRow: HTMLDivElement;
  private readonly gameplayModeRow: HTMLDivElement;
  private readonly gameplayPaletteRow: HTMLDivElement;
  private readonly gameplayNote: HTMLDivElement;
  private gameplayWave: GameplayWaveSettings = DEFAULT_GAMEPLAY_WAVE_SETTINGS;
  private readonly recordAutoRow: HTMLDivElement;
  private readonly recordFpsRow: HTMLDivElement;
  private readonly recordAudioRow: HTMLDivElement;
  private readonly recordStartButton: HTMLButtonElement;
  private readonly recordStopButton: HTMLButtonElement;
  private readonly recordCancelButton: HTMLButtonElement;
  private readonly recordDownloadButton: HTMLButtonElement;
  private readonly recordStatusLine: HTMLDivElement;
  private readonly recordErrorLine: HTMLDivElement;
  private readonly recordDoneLine: HTMLDivElement;
  private recordConfig: LiveRecordingConfig = { ...DEFAULT_RECORDING_CONFIG };
  private recordFormat = '';

  private open = false;
  private mode: InteractionMode = 'SCORE';
  private renderMode: RenderMode = 'FIXED';
  private engineEnabled = true;

  constructor(
    private readonly handlers: VisualPanelHandlers,
    parent: HTMLElement = document.body,
  ) {
    injectStyle();
    this.element = document.createElement('div');
    this.element.className = 'mandalizm-visual';
    this.element.hidden = true;

    this.toggle = document.createElement('button');
    this.toggle.type = 'button';
    this.toggle.className = 'mandalizm-visual__toggle';
    this.toggle.textContent = '▸ VISUAL';
    this.toggle.addEventListener('click', () => this.setOpen(!this.open));

    this.body = document.createElement('div');
    this.body.className = 'mandalizm-visual__body';
    this.body.hidden = true;

    // Interaction mode.
    const modeGroup = group('INTERACTION MODE');
    this.modeRow = document.createElement('div');
    this.modeRow.className = 'mandalizm-visual__row';
    for (const mode of INTERACTION_MODES) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'mandalizm-visual__chip';
      chip.textContent = mode;
      chip.dataset.mode = mode;
      chip.addEventListener('click', () => {
        this.mode = mode;
        this.syncModes();
        handlers.onInteractionMode(mode);
      });
      this.modeRow.appendChild(chip);
    }
    modeGroup.appendChild(this.modeRow);

    // Rendering mode — how the stack behaves over the length of a song.
    const renderGroup = group('VISUAL MODE');
    this.renderRow = document.createElement('div');
    this.renderRow.className = 'mandalizm-visual__row';
    for (const mode of RENDER_MODES) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'mandalizm-visual__chip';
      chip.textContent = mode;
      chip.dataset.render = mode;
      chip.addEventListener('click', () => {
        this.renderMode = mode;
        this.syncRenderModes();
        handlers.onRenderMode(mode);
      });
      this.renderRow.appendChild(chip);
    }
    renderGroup.appendChild(this.renderRow);

    // Seed — typed in directly, because reproducing a run means naming its seed.
    const seedGroup = group('SEED');
    const seedRow = document.createElement('div');
    seedRow.className = 'mandalizm-visual__row';
    this.seedInput = document.createElement('input');
    this.seedInput.type = 'number';
    this.seedInput.className = 'mandalizm-visual__seed';
    this.seedInput.addEventListener('change', () => {
      const value = Number(this.seedInput.value);
      if (Number.isFinite(value)) handlers.onSeed(value | 0);
    });
    seedRow.append(this.seedInput, chipButton('RANDOMIZE', () => handlers.onRandomize()));
    seedGroup.appendChild(seedRow);

    // Engine on/off — the regression control, and worth one click to reach.
    const engineGroup = group('VISUAL ENGINE');
    const engineRow = document.createElement('div');
    engineRow.className = 'mandalizm-visual__row';
    this.enableChip = document.createElement('button');
    this.enableChip.type = 'button';
    this.enableChip.className = 'mandalizm-visual__chip';
    this.enableChip.textContent = 'ENABLED';
    this.enableChip.setAttribute('aria-pressed', 'true');
    this.enableChip.addEventListener('click', () => {
      this.engineEnabled = !this.engineEnabled;
      this.enableChip.textContent = this.engineEnabled ? 'ENABLED' : 'DISABLED';
      this.enableChip.setAttribute('aria-pressed', String(this.engineEnabled));
      handlers.onEngineEnabled(this.engineEnabled);
    });
    engineRow.appendChild(this.enableChip);
    engineGroup.appendChild(engineRow);

    // Layers.
    const layerGroup = group('VISUAL LAYERS');
    this.layerList = document.createElement('div');
    layerGroup.appendChild(this.layerList);
    this.addRow = document.createElement('div');
    this.addRow.className = 'mandalizm-visual__row';
    layerGroup.appendChild(this.addRow);

    // Recipe.
    const recipeGroup = group('RECIPE');
    const recipeRow = document.createElement('div');
    recipeRow.className = 'mandalizm-visual__row';
    recipeRow.append(
      chipButton('SAVE', () => handlers.onSaveRecipe()),
      chipButton('LOAD', () => this.loadInput.click()),
      chipButton('RANDOMIZE', () => handlers.onRandomize()),
    );
    this.loadInput = document.createElement('input');
    this.loadInput.type = 'file';
    this.loadInput.accept = '.json,application/json';
    this.loadInput.hidden = true;
    this.loadInput.addEventListener('change', () => {
      const file = this.loadInput.files?.[0];
      this.loadInput.value = '';
      if (file !== undefined) handlers.onLoadRecipe(file);
    });
    recipeGroup.append(recipeRow, this.loadInput);

    // Replay. Two rows: the file, and which interpretation to play it through —
    // the second is the part that makes a replay a source rather than a video.
    const replayGroup = group('REPLAY');
    const replayRow = document.createElement('div');
    replayRow.className = 'mandalizm-visual__row';
    // Named for what they write. "SAVE" next to a recording section reads as
    // "save the video", and these save neither video nor audio — they save the
    // performance as data.
    this.saveReplayButton = chipButton('SAVE REPLAY JSON', () => handlers.onSaveReplay());
    replayRow.append(this.saveReplayButton, chipButton('LOAD REPLAY JSON', () => this.replayInput.click()));

    this.replayInput = document.createElement('input');
    this.replayInput.type = 'file';
    this.replayInput.accept = '.json,application/json';
    this.replayInput.hidden = true;
    this.replayInput.addEventListener('change', () => {
      const file = this.replayInput.files?.[0];
      this.replayInput.value = '';
      if (file !== undefined) handlers.onLoadReplay(file);
    });

    const playRow = document.createElement('div');
    playRow.className = 'mandalizm-visual__row';
    this.replayButtons = [
      chipButton('▶ ORIGINAL', () => handlers.onPlayReplay('original')),
      chipButton('▶ CURRENT', () => handlers.onPlayReplay('current')),
      chipButton('▶ RANDOM', () => handlers.onPlayReplay('randomize')),
    ];
    playRow.append(...this.replayButtons);

    this.replayNote = document.createElement('div');
    this.replayNote.className = 'mandalizm-visual__note';
    replayGroup.append(replayRow, playRow, this.replayInput, this.replayNote);

    // Gameplay waves. Deliberately below the layer list and visually set apart:
    // these choose how the thing the player taps is drawn, which is a different
    // question from what the background is made of.
    const gameplayGroup = group('GAMEPLAY WAVE');
    gameplayGroup.classList.add('mandalizm-visual__group--gameplay');

    this.gameplayStyleRow = document.createElement('div');
    this.gameplayStyleRow.className = 'mandalizm-visual__row';
    for (const style of GAMEPLAY_WAVE_STYLES) {
      const chip = chipButton(style, () => {
        // Picking a style means FIXED. Choosing SMOKE and having it stay on
        // SHUFFLE would make the control look broken.
        this.updateGameplay({ mode: 'FIXED', defaultStyle: style });
      });
      chip.dataset.gwStyle = style;
      this.gameplayStyleRow.appendChild(chip);
    }

    this.gameplayModeRow = document.createElement('div');
    this.gameplayModeRow.className = 'mandalizm-visual__row';
    for (const mode of GAMEPLAY_WAVE_MODES) {
      const chip = chipButton(MODE_LABELS[mode], () => {
        // WEIGHTED with an empty table would fall back to the default style and
        // look like nothing happened, so selecting it seeds a sensible table.
        const weights =
          mode === 'WEIGHTED_RANDOM' && Object.keys(this.gameplayWave.styles).length === 0
            ? { styles: { ...SUGGESTED_GAMEPLAY_WEIGHTS } }
            : {};
        this.updateGameplay({ mode, ...weights });
      });
      chip.dataset.gwMode = mode;
      this.gameplayModeRow.appendChild(chip);
    }

    // Palette. In the same section as the styles, because it is part of the
    // same decision — how the thing coming at the player looks — and nowhere
    // near the background layer list, which has its own palette per layer.
    this.gameplayPaletteRow = document.createElement('div');
    this.gameplayPaletteRow.className = 'mandalizm-visual__row';
    for (const id of PALETTE_IDS) {
      const chip = chipButton(id === 'RANDOM_PIGMENT' ? 'RANDOM' : id, () => {
        this.updateGameplay({ palette: id });
      });
      chip.classList.add('mandalizm-visual__chip--palette');
      chip.dataset.gwPalette = id;

      // The swatch. Built from the same `resolvePalette` the renderers use, so
      // what the panel shows is what will actually be painted rather than a
      // second table that could drift from it.
      const swatch = document.createElement('span');
      swatch.className = 'mandalizm-visual__swatch';
      const palette = resolvePalette(id, this.gameplayWave.seed);
      for (let index = 0; index < Math.min(5, palette.colors.length); index += 1) {
        const cell = document.createElement('span');
        const colour = paletteColor(palette, index);
        // Lifted for display only. The palette is authored dark so the tone
        // curve keeps it saturated; at those values a 5px swatch reads black.
        cell.style.background = swatchCss(colour);
        swatch.appendChild(cell);
      }
      chip.appendChild(swatch);
      this.gameplayPaletteRow.appendChild(chip);
    }

    this.gameplayNote = document.createElement('div');
    this.gameplayNote.className = 'mandalizm-visual__gameplay-note';

    const modeLabel = document.createElement('span');
    modeLabel.className = 'mandalizm-visual__label';
    modeLabel.textContent = 'MODE';
    modeLabel.style.marginTop = '7px';
    const paletteLabel = document.createElement('span');
    paletteLabel.className = 'mandalizm-visual__label';
    paletteLabel.textContent = 'PALETTE';
    paletteLabel.style.marginTop = '7px';
    gameplayGroup.append(
      this.gameplayStyleRow, modeLabel, this.gameplayModeRow,
      paletteLabel, this.gameplayPaletteRow, this.gameplayNote,
    );

    // Live recording. Its own box, below the replay controls and visibly
    // separate from them — see the CSS note.
    const recordGroup = group('RECORDING');
    recordGroup.classList.add('mandalizm-visual__group--record');

    this.recordAutoRow = document.createElement('div');
    this.recordAutoRow.className = 'mandalizm-visual__row';
    for (const [label, value] of [['AUTO ON', true], ['AUTO OFF', false]] as const) {
      const chip = chipButton(label, () => this.updateRecording({ autoRecord: value }));
      chip.dataset.recAuto = String(value);
      this.recordAutoRow.appendChild(chip);
    }

    this.recordFpsRow = document.createElement('div');
    this.recordFpsRow.className = 'mandalizm-visual__row';
    for (const fps of RECORD_FPS) {
      const chip = chipButton(`${fps} fps`, () => this.updateRecording({ fps: fps as RecordFps }));
      chip.dataset.recFps = String(fps);
      this.recordFpsRow.appendChild(chip);
    }

    this.recordAudioRow = document.createElement('div');
    this.recordAudioRow.className = 'mandalizm-visual__row';
    for (const [label, value] of [['AUDIO ON', true], ['AUDIO OFF', false]] as const) {
      const chip = chipButton(label, () => this.updateRecording({ audio: value }));
      chip.dataset.recAudio = String(value);
      this.recordAudioRow.appendChild(chip);
    }

    const recordActions = document.createElement('div');
    recordActions.className = 'mandalizm-visual__row';
    this.recordStartButton = chipButton('● START RECORD', () => handlers.onStartRecording());
    this.recordStartButton.classList.add('mandalizm-visual__chip--rec');
    this.recordStopButton = chipButton('■ STOP', () => handlers.onStopRecording());
    this.recordStopButton.disabled = true;
    this.recordCancelButton = chipButton('CANCEL', () => handlers.onCancelRecording());
    this.recordCancelButton.disabled = true;
    recordActions.append(this.recordStartButton, this.recordStopButton, this.recordCancelButton);

    const recordSecond = document.createElement('div');
    recordSecond.className = 'mandalizm-visual__row';
    this.recordDownloadButton = chipButton('DOWNLOAD RECORDING', () => handlers.onDownloadRecording());
    this.recordDownloadButton.hidden = true;
    recordSecond.appendChild(this.recordDownloadButton);

    this.recordStatusLine = document.createElement('div');
    this.recordStatusLine.className = 'mandalizm-visual__rec-status';
    this.recordErrorLine = document.createElement('div');
    this.recordErrorLine.className = 'mandalizm-visual__rec-error';
    this.recordErrorLine.hidden = true;
    this.recordDoneLine = document.createElement('div');
    this.recordDoneLine.className = 'mandalizm-visual__rec-done';
    this.recordDoneLine.hidden = true;

    const fpsLabel = document.createElement('span');
    fpsLabel.className = 'mandalizm-visual__label';
    fpsLabel.textContent = 'RECORD FPS';
    fpsLabel.style.marginTop = '7px';
    recordGroup.append(
      this.recordAutoRow, fpsLabel, this.recordFpsRow, this.recordAudioRow,
      recordActions, recordSecond,
      this.recordStatusLine, this.recordErrorLine, this.recordDoneLine,
    );

    // The artwork's own condition. Shown because "the run changed the piece" is
    // hard to believe from the picture alone while the mappings are being tuned.
    this.artRow = document.createElement('div');
    this.artRow.className = 'mandalizm-visual__art';

    this.note = document.createElement('div');
    this.note.className = 'mandalizm-visual__note';

    this.body.append(
      modeGroup,
      renderGroup,
      seedGroup,
      engineGroup,
      layerGroup,
      recipeGroup,
      replayGroup,
      gameplayGroup,
      recordGroup,
      this.artRow,
      this.note,
    );
    this.element.append(this.toggle, this.body);
    parent.appendChild(this.element);
    this.syncModes();
    this.syncRenderModes();
    this.syncGameplay();
    this.syncRecording();
  }

  // -------------------------------------------------------------------------
  // Recording
  // -------------------------------------------------------------------------

  setRecordingConfig(config: LiveRecordingConfig): void {
    this.recordConfig = config;
    this.syncRecording();
  }

  /** The format the browser actually chose, once a recording has been built. */
  setRecordingFormat(label: string): void {
    this.recordFormat = label;
    this.syncRecording();
  }

  setRecordingStatus(status: LiveRecordingStatus | null): void {
    if (status === null) {
      this.recordStatusLine.textContent = '';
      this.recordStopButton.disabled = true;
      this.recordCancelButton.disabled = true;
      this.recordStartButton.disabled = false;
      return;
    }
    const running = status.state === 'RECORDING' || status.state === 'STOPPING';
    this.recordStartButton.disabled = running;
    this.recordStopButton.disabled = status.state !== 'RECORDING';
    this.recordCancelButton.disabled = !running;

    const size = status.bytes > 0 ? ` · ${recordingSizeLabel(status.bytes)}` : '';
    const sound = status.hasAudio ? '' : ' · no audio';
    const source = status.automatic ? ' · auto' : '';
    switch (status.state) {
      case 'RECORDING':
        this.recordStatusLine.innerHTML =
          `<b>● RECORDING</b> ${elapsedLabel(status.elapsedSeconds)}${size}${sound}${source}`;
        break;
      case 'STOPPING':
        this.recordStatusLine.textContent = 'Finishing…';
        break;
      case 'DONE': {
        // The format is worth saying: a live capture is whatever MediaRecorder
        // would write, which is not necessarily what the offline export makes.
        const format = this.recordFormat.length > 0 ? ` · ${this.recordFormat}` : '';
        this.recordStatusLine.textContent =
          `Done · ${elapsedLabel(status.elapsedSeconds)}${size}${format}`;
        break;
      }
      case 'CANCELLED':
        this.recordStatusLine.textContent = 'Cancelled';
        break;
      case 'FAILED':
        this.recordStatusLine.textContent = 'Failed';
        break;
      default:
        this.recordStatusLine.textContent = '';
    }
    if (status.error !== null) this.setRecordingError(status.error);
  }

  setRecordingError(message: string | null): void {
    this.recordErrorLine.textContent = message ?? '';
    this.recordErrorLine.hidden = message === null;
  }

  setRecordingResult(summary: RecordingSummary | null): void {
    if (summary === null) {
      this.recordDoneLine.hidden = true;
      this.recordDownloadButton.hidden = true;
      return;
    }
    const name = document.createElement('b');
    name.textContent = summary.fileName;
    const detail = document.createElement('div');
    detail.textContent =
      `${summary.width}×${summary.height} · ${summary.fps} fps · ` +
      `${elapsedLabel(summary.durationSeconds)} · ${recordingSizeLabel(summary.bytes)} · ` +
      `${summary.hasAudio ? 'with audio' : 'no audio'}`;
    this.recordDoneLine.replaceChildren(name, detail);
    this.recordDoneLine.hidden = false;
    this.recordDownloadButton.hidden = false;
  }

  private updateRecording(change: Partial<LiveRecordingConfig>): void {
    this.recordConfig = { ...this.recordConfig, ...change };
    this.syncRecording();
    this.handlers.onRecordingConfig(this.recordConfig);
  }

  private syncRecording(): void {
    const config = this.recordConfig;
    press(this.recordAutoRow, 'recAuto', String(config.autoRecord));
    press(this.recordFpsRow, 'recFps', String(config.fps));
    press(this.recordAudioRow, 'recAudio', String(config.audio));
  }

  /** Reflects settings that came from a recipe rather than from a click. */
  setGameplayWave(settings: GameplayWaveSettings): void {
    this.gameplayWave = settings;
    this.syncGameplay();
  }

  private updateGameplay(change: Partial<GameplayWaveSettings>): void {
    this.gameplayWave = { ...this.gameplayWave, ...change };
    this.syncGameplay();
    this.handlers.onGameplayWave(this.gameplayWave);
  }

  /**
   * Rebuilds the generated palette's swatch.
   *
   * Only `RANDOM_PIGMENT` needs it — the fixed palettes do not change — but it
   * needs it whenever the seed does, or the chip advertises colours the run will
   * not use.
   */
  private refreshSwatches(): void {
    for (const chip of Array.from(this.gameplayPaletteRow.children) as HTMLButtonElement[]) {
      if (chip.dataset.gwPalette !== 'RANDOM_PIGMENT') continue;
      const swatch = chip.querySelector('.mandalizm-visual__swatch');
      if (swatch === null) continue;
      const palette = resolvePalette('RANDOM_PIGMENT', this.gameplayWave.seed);
      const cells = Array.from(swatch.children) as HTMLElement[];
      for (let index = 0; index < cells.length; index += 1) {
        (cells[index] as HTMLElement).style.background = swatchCss(paletteColor(palette, index));
      }
    }
  }

  private syncGameplay(): void {
    const settings = this.gameplayWave;
    for (const chip of Array.from(this.gameplayStyleRow.children) as HTMLButtonElement[]) {
      // Only meaningful in FIXED: in every other mode the style is chosen per
      // wave, so highlighting one would claim something untrue.
      const active = settings.mode === 'FIXED' && chip.dataset.gwStyle === settings.defaultStyle;
      chip.setAttribute('aria-pressed', String(active));
      chip.disabled = false;
    }
    for (const chip of Array.from(this.gameplayModeRow.children) as HTMLButtonElement[]) {
      chip.setAttribute('aria-pressed', String(chip.dataset.gwMode === settings.mode));
    }
    for (const chip of Array.from(this.gameplayPaletteRow.children) as HTMLButtonElement[]) {
      chip.setAttribute('aria-pressed', String(chip.dataset.gwPalette === settings.palette));
    }
    this.refreshSwatches();
    this.gameplayNote.textContent = describeGameplay(settings);
  }

  setOpen(open: boolean): void {
    const opening = open && !this.open;
    const closing = !open && this.open;
    this.open = open;
    this.body.hidden = !open;
    this.toggle.textContent = open ? '▾ VISUAL' : '▸ VISUAL';
    if (opening || closing) this.handlers.onOpenChanged?.(open);
  }

  get isOpen(): boolean {
    return this.open;
  }

  setVisible(visible: boolean): void {
    this.element.hidden = !visible;
  }

  setInteractionMode(mode: InteractionMode): void {
    this.mode = mode;
    this.syncModes();
  }

  setNote(text: string): void {
    this.note.textContent = text;
  }

  /** Enables the replay controls only when there is a replay to act on. */
  setReplayAvailable(available: boolean, note: string): void {
    this.saveReplayButton.disabled = !available;
    for (const button of this.replayButtons) button.disabled = !available;
    this.replayNote.textContent = note;
  }

  /**
   * Draws the artwork's condition. Called on a timer, not per frame.
   *
   * Takes `ArtStateValues` rather than a string-keyed record so that adding a
   * field to the state shows up here automatically, and so the panel cannot be
   * handed something that merely looks like one.
   */
  setArtState(state: ArtStateValues): void {
    this.artRow.replaceChildren(
      ...Object.entries(state).map(([name, value]) => {
        const cell = document.createElement('div');
        const label = document.createElement('span');
        label.textContent = name;
        const read = document.createElement('b');
        read.textContent = value.toFixed(2);
        cell.append(label, read);
        return cell;
      }),
    );
  }

  /** Offers one ADD button per renderer type this build actually has. */
  setAvailableRenderers(types: readonly string[]): void {
    this.addRow.replaceChildren(
      ...types.map((type) =>
        chipButton(`+ ${type}`, () => this.handlers.onAddLayer(type)),
      ),
    );
  }

  /** Redraws the layer list from the engine's own report. */
  setLayers(layers: readonly VisualLayerReport[]): void {
    this.layerList.replaceChildren(
      ...layers.map((entry) => this.buildLayerRow(entry)),
    );
  }

  private buildLayerRow(entry: VisualLayerReport): HTMLDivElement {
    const row = document.createElement('div');
    row.className = entry.disabledByFault
      ? 'mandalizm-visual__layer mandalizm-visual__layer--faulted'
      : 'mandalizm-visual__layer';

    const head = document.createElement('div');
    head.className = 'mandalizm-visual__head';

    const enable = document.createElement('input');
    enable.type = 'checkbox';
    enable.checked = entry.enabled && !entry.disabledByFault;
    enable.disabled = entry.disabledByFault;
    enable.addEventListener('change', () =>
      this.handlers.onLayerEnabled(entry.id, enable.checked),
    );

    const name = document.createElement('span');
    name.className = 'mandalizm-visual__name';
    name.textContent = `${entry.renderer} · z${entry.zDepth}`;

    head.append(enable, name, chipButton('✕', () => this.handlers.onRemoveLayer(entry.id)));
    row.appendChild(head);

    row.appendChild(
      slider('OPACITY', entry.opacity, 0, 1, (value) =>
        this.handlers.onLayerOpacity(entry.id, value),
      ),
    );
    row.appendChild(
      slider('INTENSITY', entry.intensity, 0, 3, (value) =>
        this.handlers.onLayerIntensity(entry.id, value),
      ),
    );

    if (entry.lastError !== null) {
      const error = document.createElement('div');
      error.className = 'mandalizm-visual__error';
      error.textContent = entry.disabledByFault
        ? `disabled after ${entry.faults} faults — ${entry.lastError}`
        : entry.lastError;
      row.appendChild(error);
    }
    return row;
  }

  setRenderMode(mode: RenderMode): void {
    this.renderMode = mode;
    this.syncRenderModes();
  }

  setSeed(seed: number): void {
    // Only written when the field is not being edited, so typing a seed is not
    // fought by the refresh that follows every recipe change.
    if (document.activeElement !== this.seedInput) this.seedInput.value = String(seed);
  }

  private syncModes(): void {
    for (const chip of this.modeRow.querySelectorAll('button')) {
      chip.setAttribute('aria-pressed', String(chip.dataset.mode === this.mode));
    }
  }

  private syncRenderModes(): void {
    for (const chip of this.renderRow.querySelectorAll('button')) {
      chip.setAttribute('aria-pressed', String(chip.dataset.render === this.renderMode));
    }
  }

  dispose(): void {
    this.element.remove();
  }
}

function group(label: string): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'mandalizm-visual__group';
  const heading = document.createElement('span');
  heading.className = 'mandalizm-visual__label';
  heading.textContent = label;
  wrapper.appendChild(heading);
  return wrapper;
}

function chipButton(label: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'mandalizm-visual__chip';
  button.textContent = label;
  button.addEventListener('click', onClick);
  return button;
}

function slider(
  label: string,
  value: number,
  min: number,
  max: number,
  onChange: (value: number) => void,
): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'mandalizm-visual__slider';
  const name = document.createElement('span');
  name.className = 'mandalizm-visual__label';
  name.textContent = label;
  const input = document.createElement('input');
  input.type = 'range';
  input.min = String(min);
  input.max = String(max);
  input.step = '0.01';
  input.value = String(value);
  const read = document.createElement('span');
  read.className = 'mandalizm-visual__value';
  read.textContent = value.toFixed(2);
  input.addEventListener('input', () => {
    const next = Number(input.value);
    read.textContent = next.toFixed(2);
    onChange(next);
  });
  row.append(name, input, read);
  return row;
}

/** Marks the chip whose data attribute matches, and clears the rest. */
function press(container: HTMLElement, key: string, value: string): void {
  for (const child of Array.from(container.children) as HTMLElement[]) {
    child.setAttribute('aria-pressed', String(child.dataset[key] === value));
  }
}

function injectStyle(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}
